export type Category = "YOGA" | "PDC" | "EMPLOYEE";

export type RegisterFilter = "all" | "yoga" | "pdc" | "employees";

export type ShiftTemplate = "morning_only" | "split_9_1_5_9" | "full_day";

export type SessionKey =
  | "morning"
  | "morning_block"
  | "evening_block"
  | "employee_shift"
  | `shift_block_${number}`;

export type VerifyMode = "face" | "fingerprint" | "unknown";

export type CellStatus = "P" | "L" | "A" | "O" | "H" | "pending";

export type NoticeType = "sick" | "oos" | "travel" | "other";

export type NoticeSession =
  | "morning"
  | "morning_block"
  | "evening_block"
  | "whole_day";

export interface Person {
  id: string;
  name: string;
  phone: string;
  device_pin: string;
  categories: Category[];
  person_type: string;
  shift_templates: Partial<Record<Category, ShiftTemplate>>;
  active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface PersonInput {
  name: string;
  phone: string;
  device_pin: string;
  categories: Category[];
  shift_templates?: Partial<Record<Category, ShiftTemplate>>;
  active?: boolean;
}

export interface RegisterColumn {
  key: SessionKey;
  label: string;
  category: Category;
}

export interface AttendanceCell {
  status: CellStatus;
  late_minutes?: number | null;
  first_punch_at?: string | null;
  verify_mode?: VerifyMode | null;
  has_notice?: boolean;
  notice_reason?: string | null;
  is_overridden?: boolean;
  override_reason?: string | null;
  overridden_by?: string | null;
  holiday_name?: string | null;
}

export interface RegisterRow {
  person_id: string;
  person_name: string;
  category: Category;
  columns: Partial<Record<SessionKey, AttendanceCell | null>>;
  shift_label?: string | null;
}

export interface RegisterGroup {
  category: Category;
  label: string;
  rows: RegisterRow[];
}

export interface DailyRegister {
  date: string;
  columns: RegisterColumn[];
  groups: RegisterGroup[];
  morning_finalized: boolean;
  holiday?: Holiday | null;
}

export type HolidayScope = "all" | "devotees" | "employees";

export interface Holiday {
  id: string;
  date: string;
  name: string;
  applies_to: HolidayScope;
}

export interface MonthlyCell {
  status: CellStatus;
  late_minutes?: number | null;
  holiday_name?: string | null;
}

export interface MonthlyRow {
  person_id: string;
  person_name: string;
  category?: Category;
  cells: Record<string, MonthlyCell>;
  summary: MonthlyRowSummary;
}

export interface MonthlyRowSummary {
  present: number;
  late: number;
  absent: number;
  on_leave: number;
  holiday: number;
  pending: number;
  accountable_days: number;
  present_pct: number | null;
  absent_pct: number | null;
}

export interface MonthlyTable {
  key: string;
  label: string;
  rows: MonthlyRow[];
}

export interface MonthlyRegister {
  month: string;
  dates: string[];
  tables: MonthlyTable[];
  holidays?: Holiday[];
}

export interface EmployeeShiftBlock {
  id?: string;
  sort_order: number;
  start_time: string;
  end_time: string;
  report_by_time?: string;
  label?: string;
}

export interface EmployeeShiftRow {
  person_id: string;
  person_name: string;
  has_custom_shift: boolean;
  default_blocks?: EmployeeShiftBlock[];
  blocks: EmployeeShiftBlock[];
}

export interface EmployeeShiftList {
  date: string;
  rows: EmployeeShiftRow[];
}

export interface DashboardKpis {
  present: number;
  late: number;
  absent: number;
  pending: number;
  on_leave: number;
}

export interface DevoteeAttendee {
  person_id: string;
  person_name: string;
  category: Category;
  status: CellStatus;
  late_minutes?: number | null;
  first_punch_at?: string | null;
  has_notice?: boolean;
}

export interface DevoteeAttendance {
  label: string;
  date: string;
  kpis: DashboardKpis;
  attendees: DevoteeAttendee[];
}

export interface PunchFeedItem {
  id: string;
  person_name: string;
  category: Category;
  session: SessionKey;
  punched_at: string;
  verify_mode: VerifyMode;
  status: CellStatus;
  late_minutes?: number | null;
}

export interface Dashboard {
  date: string;
  kpis: DashboardKpis;
  devotee_attendance?: DevoteeAttendance | null;
  recent_punches: PunchFeedItem[];
  action_inbox?: DashboardActionInbox;
}

export interface DashboardNoPunchItem {
  person_id: string;
  person_name: string;
  category: Category;
  status: CellStatus;
  session: SessionKey;
  has_notice: boolean;
}

export interface DashboardActionInbox {
  pending_notices_count: number;
  pending_notices: Notice[];
  no_punch_count: number;
  no_punch_today: DashboardNoPunchItem[];
}

export type NoticeApprovalStatus = "submitted" | "approved" | "rejected";

export interface Notice {
  id: string;
  person_id: string;
  person_name: string;
  date: string;
  session: NoticeSession;
  notice_type: NoticeType;
  reason: string;
  notes?: string | null;
  approval_status: NoticeApprovalStatus;
  seen: boolean;
  created_at: string;
  reviewed_at?: string | null;
}

export interface NoticeInput {
  date: string;
  session: NoticeSession;
  notice_type: NoticeType;
  reason: string;
  notes?: string;
}

export interface MeSessionStatus {
  session: SessionKey | "whole_day";
  label: string;
  status: CellStatus;
  late_minutes?: number | null;
  first_punch_at?: string | null;
}

export interface MeToday {
  date: string;
  person_name: string;
  categories: Category[];
  sessions: MeSessionStatus[];
  morning_finalized: boolean;
}

export interface HistoryDay {
  date: string;
  status: CellStatus;
  late_minutes?: number | null;
  holiday_name?: string | null;
  sessions?: Partial<Record<SessionKey, AttendanceCell>>;
}

export interface MeHistory {
  month: string;
  days: HistoryDay[];
  holidays?: Holiday[];
}

export interface MeLoginResponse {
  person_id: number;
  name: string;
  device_pin: string;
  phone: string;
  person_categories: Category[];
}

export interface MeSession {
  person_id: number;
  name: string;
  phone: string;
  device_pin: string;
  person_categories: Category[];
}

export const CATEGORY_LABELS: Record<Category, string> = {
  YOGA: "Morning Yoga",
  PDC: "PDC",
  EMPLOYEE: "Employees",
};

export const NOTICE_REASONS = [
  "Sick / unwell",
  "Out of station",
  "Family emergency",
  "Temple service elsewhere",
  "Doctor appointment",
  "Other",
] as const;

export const NOTICE_TYPES: { value: NoticeType; label: string }[] = [
  { value: "sick", label: "Sick leave" },
  { value: "oos", label: "Out of station" },
  { value: "travel", label: "Travel" },
  { value: "other", label: "Other" },
];

export const SHIFT_TEMPLATES: { value: ShiftTemplate; label: string }[] = [
  { value: "morning_only", label: "Morning only" },
  { value: "split_9_1_5_9", label: "Split (9–1 & 5–9)" },
  { value: "full_day", label: "Full day" },
];
